import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { getGroupBookings, adminUpdateGroupBooking, saveGroupBookingMemo } from '../../services/groupBookingService';
import { GroupBooking, GroupBookingStatus } from '../../types/groupBooking';
import * as XLSX from 'xlsx';
import { formatGroupDateShort, formatGroupDateTime } from '../../utils/dateUtils';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'pending', label: '승인대기' },
  { value: 'confirmed', label: '신청확정' },
  { value: 'cancelled', label: '신청취소' },
];

const getStatusBadge = (status: GroupBookingStatus) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'confirmed':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'cancelled':
      return 'bg-gray-100 text-gray-500 border-gray-200';
  }
};

const getStatusLabel = (status: GroupBookingStatus) => {
  switch (status) {
    case 'pending': return '승인대기';
    case 'confirmed': return '신청확정';
    case 'cancelled': return '신청취소';
  }
};

const formatDate = formatGroupDateShort;
const formatDateTime = formatGroupDateTime;

const GroupBookingManagement: React.FC = () => {
  const [allBookings, setAllBookings] = useState<GroupBooking[]>([]);
  const [bookings, setBookings] = useState<GroupBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [detailBooking, setDetailBooking] = useState<GroupBooking | null>(null);
  const [memoText, setMemoText] = useState('');
  const [memoSaving, setMemoSaving] = useState(false);

  // 검색 디바운스
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // 전체 데이터 (카운트용) — 검색어만 반영
  const fetchAllBookings = useCallback(async () => {
    const data = await getGroupBookings({
      status: 'all',
      search: debouncedSearch || undefined,
    });
    setAllBookings(data);
  }, [debouncedSearch]);

  // 필터된 데이터 (테이블용)
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const data = await getGroupBookings({
      status: statusFilter,
      search: debouncedSearch || undefined,
    });
    setBookings(data);
    setLoading(false);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchAllBookings();
  }, [fetchAllBookings]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleStatusChange = async (bookingId: string, newStatus: 'confirmed' | 'cancelled') => {
    const action = newStatus === 'confirmed' ? '신청을 확정' : '신청을 취소';
    if (!window.confirm(`이 ${action}하시겠습니까?`)) return;

    setActionLoading(bookingId);

    const result = await adminUpdateGroupBooking({
      booking_id: bookingId,
      new_status: newStatus,
    });

    if (result.success) {
      if (result.data?.auto_cancelled && result.data.auto_cancelled.length > 0) {
        const names = result.data.auto_cancelled.map(ac => ac.institution_name).join(', ');
        alert(`확정 완료. 같은 날짜의 다른 신청이 자동 취소되었습니다: ${names}`);
      }
      await Promise.all([fetchBookings(), fetchAllBookings()]);
    } else {
      alert(result.error?.message || '처리에 실패했습니다.');
    }

    setActionLoading(null);
  };

  const openDetail = (booking: GroupBooking) => {
    setDetailBooking(booking);
    setMemoText(booking.admin_memo || '');
  };

  const handleSaveMemo = async () => {
    if (!detailBooking) return;
    setMemoSaving(true);
    const ok = await saveGroupBookingMemo(detailBooking.id, memoText);
    if (ok) {
      setDetailBooking({ ...detailBooking, admin_memo: memoText });
      await Promise.all([fetchBookings(), fetchAllBookings()]);
    } else {
      alert('메모 저장에 실패했습니다.');
    }
    setMemoSaving(false);
  };

  // 엑셀 다운로드
  const exportExcel = () => {
    const excelData = bookings.map((b, index) => ({
      '번호': index + 1,
      '예약번호': b.booking_number,
      '프로그램': b.program_name,
      '단체명': b.institution_name,
      '기관전화': b.institution_phone,
      '예약자명': b.booker_name,
      '예약자 휴대폰': b.booker_phone,
      '유형': b.participants.type === 'preschool' ? '유치원' : '초등',
      '인원': b.participants.total_count,
      '상세인원': b.participants.groups.map(g => `${g.label} ${g.count}명`).join(', '),
      '예약날짜': b.booking_dates.sort().join(', '),
      '상태': getStatusLabel(b.status),
      '신청일시': formatDateTime(b.created_at),
      '확정일시': b.confirmed_at ? formatDateTime(b.confirmed_at) : '',
      '관리자메모': b.admin_memo || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet['!cols'] = [
      { wch: 5 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
      { wch: 10 }, { wch: 15 }, { wch: 8 }, { wch: 6 }, { wch: 30 },
      { wch: 30 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 20 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '단체체험 신청목록');

    const now = new Date();
    const fileName = `단체체험_신청목록_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const statusCounts = {
    all: allBookings.length,
    pending: allBookings.filter(b => b.status === 'pending').length,
    confirmed: allBookings.filter(b => b.status === 'confirmed').length,
    cancelled: allBookings.filter(b => b.status === 'cancelled').length,
  };

  // 필터 결과 (search는 서버에서 처리되지만 statusFilter 탭은 클라이언트에서도 적용)
  const filteredBookings = bookings;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">단체체험 예약 관리</h1>
          <p className="text-gray-600 text-sm">단체체험 예약 신청을 확인하고 승인/취소 처리합니다.</p>
        </div>
        <button
          onClick={exportExcel}
          className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          엑셀 다운로드
        </button>
      </div>

      {/* 상태 탭 */}
      <div className="flex gap-2 mb-4">
        {STATUS_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setStatusFilter(opt.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === opt.value
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt.label} ({statusCounts[opt.value as keyof typeof statusCounts] ?? 0})
          </button>
        ))}
      </div>

      {/* 검색 */}
      <div className="relative mb-6">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="단체명, 예약자명, 예약번호 검색..."
          className="w-full max-w-md pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* 테이블 */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">로딩 중...</div>
      ) : filteredBookings.length === 0 ? (
        <div className="text-center py-16 text-gray-400">예약 내역이 없습니다.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white rounded-xl shadow-sm border">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">예약번호</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">단체명</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">예약자</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">유형/인원</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">예약날짜</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">상태</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">신청일</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBookings.map(booking => (
                <tr key={booking.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(booking)}>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{booking.booking_number}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-800">{booking.institution_name}</div>
                    <div className="text-xs text-gray-400">{booking.institution_phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-800">{booking.booker_name}</div>
                    <div className="text-xs text-gray-400">{booking.booker_phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-700">
                      {booking.participants.type === 'preschool' ? '유치원' : '초등'} {booking.participants.total_count}명
                    </div>
                    <div className="text-xs text-gray-400">
                      {booking.participants.groups.map(g => `${g.label} ${g.count}명`).join(', ')}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {booking.booking_dates.sort().map(d => (
                        <span key={d} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                          {formatDate(d)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(booking.status)}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {formatDateTime(booking.created_at)}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1.5">
                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(booking.id, 'confirmed')}
                            disabled={actionLoading === booking.id}
                            className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                            title="신청확정"
                          >
                            <CheckCircleIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleStatusChange(booking.id, 'cancelled')}
                            disabled={actionLoading === booking.id}
                            className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                            title="신청취소"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => handleStatusChange(booking.id, 'cancelled')}
                          disabled={actionLoading === booking.id}
                          className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                          title="신청취소"
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      )}
                      {booking.status === 'cancelled' && (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 상세 모달 */}
      {detailBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetailBooking(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">예약 상세</h3>
              <button onClick={() => setDetailBooking(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* 기본 정보 */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">예약번호</span>
                  <p className="font-mono font-semibold text-gray-800">{detailBooking.booking_number}</p>
                </div>
                <div>
                  <span className="text-gray-500">상태</span>
                  <p><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadge(detailBooking.status)}`}>{getStatusLabel(detailBooking.status)}</span></p>
                </div>
                <div>
                  <span className="text-gray-500">프로그램</span>
                  <p className="font-medium text-gray-800">{detailBooking.program_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">신청일시</span>
                  <p className="text-gray-800">{formatDateTime(detailBooking.created_at)}</p>
                </div>
              </div>

              <hr />

              {/* 기관/예약자 정보 */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">단체명</span>
                  <p className="font-medium text-gray-800">{detailBooking.institution_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">기관전화</span>
                  <p className="text-gray-800">{detailBooking.institution_phone}</p>
                </div>
                <div>
                  <span className="text-gray-500">예약자</span>
                  <p className="font-medium text-gray-800">{detailBooking.booker_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">휴대폰</span>
                  <p className="text-gray-800">{detailBooking.booker_phone}</p>
                </div>
              </div>

              <hr />

              {/* 예약 날짜 */}
              <div>
                <span className="text-sm text-gray-500">예약 날짜</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {detailBooking.booking_dates.sort().map(d => (
                    <span key={d} className="px-2.5 py-1 bg-gray-100 rounded text-xs text-gray-700 font-medium">
                      {formatDate(d)}
                    </span>
                  ))}
                </div>
              </div>

              {/* 참가자 상세 */}
              <div>
                <span className="text-sm text-gray-500">
                  참가자 ({detailBooking.participants.type === 'preschool' ? '유치원' : '초등'} / 총 {detailBooking.participants.total_count}명)
                </span>
                <div className="mt-1 bg-gray-50 rounded-lg p-3">
                  <table className="w-full text-sm">
                    <tbody>
                      {detailBooking.participants.groups.map((g, i) => (
                        <tr key={i} className="border-b last:border-0 border-gray-200">
                          <td className="py-1.5 text-gray-600">{g.label}</td>
                          <td className="py-1.5 text-right font-semibold text-gray-800">{g.count}명</td>
                        </tr>
                      ))}
                      <tr className="font-bold">
                        <td className="pt-2 text-gray-700">합계</td>
                        <td className="pt-2 text-right text-gray-900">{detailBooking.participants.total_count}명</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 확정/취소 일시 */}
              {detailBooking.confirmed_at && (
                <div className="text-sm">
                  <span className="text-gray-500">확정일시:</span> <span className="text-gray-800">{formatDateTime(detailBooking.confirmed_at)}</span>
                </div>
              )}
              {detailBooking.cancelled_at && (
                <div className="text-sm">
                  <span className="text-gray-500">취소일시:</span> <span className="text-gray-800">{formatDateTime(detailBooking.cancelled_at)}</span>
                </div>
              )}

              <hr />

              {/* 관리자 메모 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">관리자 메모</label>
                <textarea
                  value={memoText}
                  onChange={e => setMemoText(e.target.value)}
                  rows={3}
                  placeholder="관리자 내부 메모를 입력하세요..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleSaveMemo}
                    disabled={memoSaving}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      memoSaving
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {memoSaving ? '저장 중...' : '메모 저장'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupBookingManagement;
